import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input } from '../components/ui';
import { ErrorState } from '../components/states';
import { confirmPasswordReset } from '../api/auth';

/**
 * A1.2/A1.3 — landing page for the reset/invite email. PocketBase validates the
 * one-time token; setting the password here completes invite acceptance too.
 */
export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await confirmPasswordReset(token, password, confirm);
      navigate('/login', { replace: true });
    } catch {
      setError('That reset link is invalid or has expired. Request a new one.');
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <main className="workspace-main">
        <ErrorState
          title="Missing reset token"
          detail="Open the link from your reset or invite email."
        />
      </main>
    );
  }

  return (
    <main className="workspace-main">
      <section className="panel auth-panel">
        <header className="panel-heading">
          <span className="index-label">AUTH / SET PASSWORD</span>
        </header>
        <div className="panel-body">
          <h1>Set your password</h1>
          {error && <ErrorState title="Could not set password" detail={error} />}
          <form onSubmit={onSubmit} className="auth-form">
            <label className="index-label" htmlFor="password">
              NEW PASSWORD
            </label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <label className="index-label" htmlFor="confirm">
              CONFIRM PASSWORD
            </label>
            <Input
              id="confirm"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={confirm}
              onChange={(event) => setConfirm(event.target.value)}
            />
            <div className="auth-actions">
              <Button type="submit" disabled={busy}>
                {busy ? 'Saving…' : 'Set password'}
              </Button>
              <Link className="auth-link" to="/login">
                Back to sign in
              </Link>
            </div>
          </form>
        </div>
      </section>
    </main>
  );
}
